import React from 'react';
import { formatPhone } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Building2, FolderTree, User, Edit, MoreHorizontal, Plus,
  Loader2, Trash2, Hash, Mail, MapPin, Copy, Check, MailCheck, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { marketingAPI } from '@/lib/api';

/** Copy helper with an execCommand fallback for non-secure contexts. */
const copyText = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

/** Small pill that copies a value to the clipboard with inline feedback. */
const CopyChip = ({ icon: Icon, label, value, testid, tone = 'slate' }) => {
  const [done, setDone] = React.useState(false);
  if (!value) return null;
  const tones = {
    slate: 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200',
    navy: 'bg-sky-50 text-sky-700 hover:bg-sky-100 border-sky-200',
  };
  const onClick = async (e) => {
    e.stopPropagation();
    const ok = await copyText(value);
    if (ok) {
      setDone(true);
      toast.success(`${label} copied`);
      setTimeout(() => setDone(false), 1500);
    } else {
      toast.error('Could not copy');
    }
  };
  return (
    <button
      type="button" onClick={onClick} data-testid={testid} title={`Copy ${label}`}
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border transition-colors ${tones[tone]}`}
    >
      {done ? <Check size={12} className="text-emerald-600" /> : <Icon size={12} />} {label}
    </button>
  );
};

/**
 * AgencyLogoWidget — inline logo uploader for KR admins.
 */
const AgencyLogoWidget = ({ agency, canEdit, onChanged }) => {
  const [src, setSrc] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    let revoke;
    if (agency?.logo_path) {
      marketingAPI.attachmentBlob(agency.logo_path).then((res) => {
        const url = URL.createObjectURL(res.data);
        revoke = url;
        setSrc(url);
      }).catch(() => setSrc(null));
    } else {
      setSrc(null);
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [agency?.logo_path]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await marketingAPI.uploadAgencyLogo(agency.agency_id, fd);
      toast.success('Agency logo updated');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this agency logo?')) return;
    try {
      await marketingAPI.deleteAgencyLogo(agency.agency_id);
      toast.success('Logo removed');
      onChanged?.();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="relative group">
      <div
        className="w-16 h-16 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0"
        style={src ? { background: '#fff', border: '1px solid var(--kr-border)' }
                    : { background: 'linear-gradient(135deg,var(--kr-navy-700),var(--kr-navy-900))', color: '#fff' }}
      >
        {src ? <img src={src} alt={`${agency.agency_name} logo`} className="max-w-full max-h-full object-contain" /> : <Building2 size={32} />}
      </div>
      {canEdit && (
        <>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="agency-logo-input" />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="absolute -bottom-1 -right-1 bg-slate-900 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
            title={src ? 'Replace logo' : 'Upload logo'}
            data-testid="upload-agency-logo-btn"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Edit size={12} />}
          </button>
          {src && (
            <button
              type="button"
              onClick={handleDelete}
              className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove logo"
              data-testid="delete-agency-logo-btn"
            >
              <Trash2 size={12} />
            </button>
          )}
        </>
      )}
    </div>
  );
};

/**
 * AgencyHeader — identity + actions strip on the Agency Command Center.
 * Mirrors AgentHeader's Phase 17.5 UX: primary action (Edit) visible + a
 * "More Actions" dropdown for secondary flows.
 */
export const AgencyHeader = ({
  agency,
  parentAgency,
  principalUsers = [],
  isKR = false,
  onEdit,
  onLogoChanged,
  onAddSubAgency,
  onUpdateInfo,
}) => {
  if (!agency) return null;
  const canAddSub = isKR && agency.depth < 5;

  const fullAddress = [
    agency.address_line1,
    agency.address_line2,
    [agency.city, agency.state].filter(Boolean).join(', '),
    agency.zip,
  ].filter(Boolean).join(' · ');

  const allInfo = [
    `Agency Name: ${agency.agency_name || ''}`,
    `NPN: ${agency.npn || ''}`,
    `EIN: ${agency.ein || ''}`,
    `Principal: ${agency.principal_agent_name || ''}`,
    `Email: ${agency.email || ''}`,
    `Phone: ${agency.phone || ''}`,
    `Address: ${[agency.address_line1, agency.address_line2, agency.city, agency.state, agency.zip].filter(Boolean).join(', ')}`,
  ].join('\n');

  return (
    <Card className="mb-6" data-testid="agency-overview-card">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <AgencyLogoWidget agency={agency} canEdit={isKR} onChanged={onLogoChanged} />
            <div className="min-w-0">
              <h2
                className="text-2xl font-heading font-semibold"
                style={{ color: 'var(--kr-navy-900)' }}
                data-testid="agency-name"
              >
                {agency.agency_name}
              </h2>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
                {parentAgency ? (
                  <span className="flex items-center gap-1">
                    <FolderTree size={14} />
                    Parent:&nbsp;
                    <Link
                      to={`/agencies/${parentAgency.agency_id}`}
                      className="hover:underline"
                      style={{ color: 'var(--kr-navy-700)' }}
                      data-testid="parent-agency-link"
                    >
                      {parentAgency.agency_name}
                    </Link>
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <FolderTree size={14} /> Root Agency
                  </span>
                )}
                <span>Depth: {agency.depth}</span>
                <StatusBadge status={agency.status} />
              </div>

              {/* Business identity + contact fields */}
              <div className="flex items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500 flex-wrap" data-testid="agency-fields">
                {agency.npn && (
                  <span className="flex items-center gap-1"><Hash size={12} /> <span className="font-mono" data-testid="agency-npn">NPN: {agency.npn}</span></span>
                )}
                {agency.ein && (
                  <span className="flex items-center gap-1"><FileText size={12} /> <span className="font-mono" data-testid="agency-ein">EIN: {agency.ein}</span></span>
                )}
                {agency.principal_agent_npn && (
                  <span className="flex items-center gap-1">
                    <User size={12} /> Principal:&nbsp;
                    <Link
                      to={`/agents/${agency.principal_agent_npn}`}
                      className="hover:underline font-medium"
                      style={{ color: 'var(--kr-navy-700)' }}
                      data-testid="agency-principal-link"
                    >
                      {agency.principal_agent_name || agency.principal_agent_npn}
                    </Link>
                  </span>
                )}
                {agency.email && <span className="flex items-center gap-1" data-testid="agency-email"><Mail size={12} /> {agency.email}</span>}
                {agency.phone && <span data-testid="agency-phone">{formatPhone(agency.phone)}</span>}
                {fullAddress && <span className="flex items-center gap-1" data-testid="agency-address"><MapPin size={12} /> {fullAddress}</span>}
              </div>

              {/* Copy toolbar — mirrors the Agent profile */}
              <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-testid="agency-copy-toolbar">
                <CopyChip icon={Mail} label="Copy Email" value={agency.email} testid="copy-agency-email" tone="navy" />
                <CopyChip icon={Hash} label="Copy NPN" value={agency.npn} testid="copy-agency-npn" />
                <CopyChip icon={FileText} label="Copy EIN" value={agency.ein} testid="copy-agency-ein" />
                <CopyChip icon={Copy} label="Copy All Info" value={allInfo} testid="copy-agency-all" />
              </div>
            </div>
          </div>

          {isKR && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="agency-actions">
              <Button size="sm" onClick={onEdit} data-testid="edit-agency-btn">
                <Edit size={14} className="mr-1" /> Edit Agency
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="agency-more-actions-btn">
                    <MoreHorizontal size={14} className="mr-1" /> More Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {canAddSub && (
                    <DropdownMenuItem onClick={onAddSubAgency} data-testid="add-sub-agency-action">
                      <Plus size={14} className="mr-2" /> Add Sub-Agency
                    </DropdownMenuItem>
                  )}
                  {onUpdateInfo && (
                    <DropdownMenuItem onClick={onUpdateInfo} data-testid="agency-update-info-btn">
                      <MailCheck size={14} className="mr-2" /> Update Agency Info
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <Link to={`/agents?agency=${agency.agency_id}`} className="block">
                    <DropdownMenuItem data-testid="view-agents-action">
                      <User size={14} className="mr-2" /> View All Agents
                    </DropdownMenuItem>
                  </Link>
                  <Link to={`/requests?agency=${agency.agency_id}`} className="block">
                    <DropdownMenuItem data-testid="view-requests-action">
                      <FolderTree size={14} className="mr-2" /> View All Requests
                    </DropdownMenuItem>
                  </Link>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {principalUsers.length > 0 && (
          <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--kr-border)' }}>
            <Label className="text-xs text-slate-500 mb-2 block">Principal Users</Label>
            <div className="flex flex-wrap gap-2">
              {principalUsers.map((u) => (
                <div key={u.user_id} className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-sm" data-testid={`principal-user-${u.user_id}`}>
                  <User size={13} />
                  <span>{u.name}</span>
                  <span className="text-xs text-slate-500">
                    ({u.roles?.find((r) => r.agency_id === agency.agency_id)?.role?.replace('_', ' ')})
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgencyHeader;
